/**
 * ContractDocsPanel — Step 7 rich detail.
 *
 * Lightweight checklist of supplier-side contract documents. Each row
 * is a doc requirement; toggling it cycles received → reviewed → signed.
 */
import React from 'react';
import { FileSignature, AlertTriangle, CheckCircle2, FileText } from 'lucide-react';

const REQUIRED_DOCS = [
  { key: 'pro_forma',    label: 'Pro forma invoice',     why: 'Establishes price, qty, terms before formal PO.' },
  { key: 'tech_specs',   label: 'Technical specifications confirmed', why: 'Supplier confirms they can meet our spec sheet.' },
  { key: 'sample',       label: 'Pre-production sample',  why: 'Physical or photo sample for QC approval.' },
  { key: 'sales_contract', label: 'Sales contract / Trade Assurance', why: 'Legal agreement between buyer and supplier.' },
];

const ContractDocsPanel = ({ order }) => {
  // Demo: derive doc state from the lifecycle stage of the order.
  // Done if we're past Step 9 (verify trade assurance), partial otherwise.
  const states = order.stepStates || {};
  const tradeAssuranceDone = states[9]?.status === 'done';
  const productionStarted = states[12]?.status !== 'pending' && states[12]?.status !== undefined;

  const docState = (key) => {
    if (tradeAssuranceDone) return 'signed';
    if (productionStarted) return 'signed';
    if (key === 'pro_forma') return 'received';
    return 'pending';
  };

  return (
    <div className="manifest-rich-panel manifest-contract-docs">
      <div className="manifest-rich-panel-head">
        <div className="manifest-rich-panel-title">
          <FileSignature size={16} /> Supplier contract documents
        </div>
        <div className="manifest-rich-panel-subtle">
          Redline cycle: review → request changes → re-issue → sign
        </div>
      </div>

      <ul className="manifest-doc-list">
        {REQUIRED_DOCS.map((d) => {
          const state = docState(d.key);
          return (
            <li key={d.key} className={`manifest-doc-row state-${state}`}>
              <div className="manifest-doc-icon">
                {state === 'signed' ? <CheckCircle2 size={18} /> :
                 state === 'received' ? <FileText size={18} /> :
                 <AlertTriangle size={18} />}
              </div>
              <div className="manifest-doc-main">
                <div className="manifest-doc-label">{d.label}</div>
                <div className="manifest-doc-why">{d.why}</div>
              </div>
              <div className={`manifest-doc-state state-${state}`}>
                {state === 'signed' ? 'Signed' : state === 'received' ? 'Received' : 'Awaiting'}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default ContractDocsPanel;
